import { Chart, TooltipModel } from "chart.js";
import { useCallback, useState } from "react";

export interface ScatterTip<P> {
  point: P;
  // the chart's canvas, and the caret's position on it in CSS pixels
  canvas: HTMLCanvasElement;
  x: number;
  y: number;
}

/**
 * Hovered point + caret position for a scatter whose tooltip is rendered by
 * React (so it can hold MUI/SVG content) rather than on the canvas. Plug
 * `externalTooltip` into `options.plugins.tooltip.external`; it maps chart.js's
 * (datasetIndex, dataIndex) back to the hovered point through the per-dataset
 * point lists the chart already builds for its zones.
 */
export const useScatterTooltip = <P extends { caseId: string }>(
  pointsOfDataset: readonly (readonly P[])[]
) => {
  const [tip, setTip] = useState<ScatterTip<P> | null>(null);

  const externalTooltip = useCallback(
    ({
      chart,
      tooltip,
    }: {
      chart: Chart<"scatter">;
      tooltip: TooltipModel<"scatter">;
    }) => {
      if (tooltip.opacity === 0) {
        setTip(null);
        return;
      }
      const dp = tooltip.dataPoints?.[0];
      const point = dp && pointsOfDataset[dp.datasetIndex]?.[dp.dataIndex];
      if (!point) return;
      const x = Math.round(tooltip.caretX);
      const y = Math.round(tooltip.caretY);
      // chart.js calls this on every pointer move; keep the previous state
      // (and skip the re-render) while the hovered dot and caret are unchanged
      setTip((prev) =>
        prev &&
        prev.point.caseId === point.caseId &&
        prev.x === x &&
        prev.y === y
          ? prev
          : { point, canvas: chart.canvas, x, y }
      );
    },
    [pointsOfDataset]
  );

  return { tip, externalTooltip };
};
